import { HttpEvent, HttpEventType, HttpProgressEvent } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Observable, throwError } from "rxjs";
import { catchError, filter, map, switchMap } from "rxjs/operators";

import { AWSHttpClient, S3FileService } from "@intocare/aws";
import { SnackbarService } from "@intocare/ui";

@Injectable()
export class UploadService {
	private service = inject(S3FileService);
	private http = inject(AWSHttpClient);
	private snackbar = inject(SnackbarService);

	upload(
		type: string,
		namespace: string,
		file: File,
		generateUniqueFilename = false,
	): Observable<HttpEvent<{ progress: number }>> {
		return this.getSignedUrl(type, namespace, file, generateUniqueFilename).pipe(
			switchMap((res: any) => {
				const { url } = res;

				return this.service.uploadWithSignedUrl(file, url, { reportProgress: true }).pipe(
					filter((event) => event.type === HttpEventType.Response || event.type === HttpEventType.UploadProgress),
					map((event) => {
						if (event.type === HttpEventType.Response) {
							// Return the original `getSignedUrl` response which also has all the information regarding url and filename
							return res;
						}

						const e = event as HttpProgressEvent;

						return {
							progress: (e.loaded / e.total) * 100,
							type: e.type,
						};
					}),
					catchError((error: unknown) => {
						this.snackbar.show({ text: "labels.error.uploading", error: true });

						return throwError(error);
					}),
				);
			}),
		);
	}

	private getSignedUrl(type, namespace: string, file: File, generateUniqueFilename?: boolean) {
		const extension = file.name.split(".").pop();

		return this.http.post<{ url: string; filename: string }>(`/upload/${type}/signed-url/${namespace}`, {
			extension,
			contentType: file.type,
			// If the file should be generated in the back-end, don't provide `filename` in the body
			...(generateUniqueFilename ? {} : { filename: file.name }),
		});
	}
}
